"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  orderDeliveryTypeTransalation,
  type OrderOutput,
  type OrderStatus,
  orderStatusTranslation,
  type Address,
  type TagsOutput,
  OrderDeliveryType,
  orderPaymentStatusTranslation,
  type CustomersOutput,
} from "@/interfaces";
import type {
  PaymentsOutput,
  PaymentDetailsMethod,
} from "@/interfaces/payments";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { OrderStatusUpdate } from "./components/OrderStatusUpdate";
import { OrderPayments } from "./components/OrderPayments";
import { OrderService } from "@/services/OrderService";
import { PaymentsService } from "@/services/PaymentService";
import { format } from "date-fns";
import {
  CalendarIcon,
  TruckIcon,
  DollarSignIcon,
  UserIcon,
  MapPinIcon,
  TagIcon,
  ClipboardListIcon,
  Edit,
  ClipboardIcon,
  ArrowLeft,
  Phone,
  PackageCheck,
  CreditCard,
  Printer,
} from "lucide-react";
import { OrderProductList } from "./components/OrderProductList";
import { DeleteConfirmationAlert } from "@/components/DeleteConfirmationAlert";
import { useDeleteConfirmation } from "@/hooks/use-delete-confirmation";
import OrderMarginAnalysis from "@/components/OrderMarginAnalysis";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { numberToMoney } from "@/util/moneyConversion";
import {
  formatCep,
  formatDddPhone,
  formatWhatsappLink,
} from "@/util/formatters";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { PrintOptions } from "@/components/PrintOptions";
import {
  orderToProductionReport as orderToProductionReport,
  orderToReceiptReport,
} from "@/util/report-adapters";

interface InfoItemProps {
  icon: React.ReactNode;
  title: string;
  value: string | React.ReactNode;
  valueClassName?: string;
}

function formatAddress(address: Address): string {
  const {
    line1 = "",
    number = "",
    line2 = "",
    neighborhood = "",
    city = "",
    zipCode = "",
  } = address;

  return `${line1}${number ? `, ${number}` : ""}${line2 ? ` - ${line2}` : ""},
          ${neighborhood.trim()}${
    neighborhood && city ? ", " : ""
  }${city.trim()}
          ${zipCode ? ` - ${formatCep(zipCode)}` : ""}`.trim();
}

export function OrderDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderOutput | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const ordersService = new OrderService();
  const paymentsService = new PaymentsService();

  // Usando o hook para gerenciar o estado de exclusão
  const {
    isOpen,
    itemToDelete,
    open: openDeleteDialog,
    close: closeDeleteDialog,
  } = useDeleteConfirmation<string>();

  const fetchOrderDetails = async () => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    try {
      const fetchedOrder = await ordersService.findById(id, {
        expand: ["customers", "tags", "payments"],
      });
      setOrder(fetchedOrder);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  const handleStatusUpdate = async (newStatus: OrderStatus) => {
    if (!order) return;
    try {
      setIsLoading(true);
      await ordersService.update(order.id, {
        status: newStatus,
      });
      fetchOrderDetails();
    } finally {
      setIsLoading(false);
    }
  };

  const calculateTotalProductsValue = () => {
    if (!order?.products) return 0;
    return order.products.reduce((sum, item) => {
      // Valor do produto principal
      let itemTotal = item.unitPrice * item.quantity;
      
      // Adicionar valor dos adicionais se existirem
      if (item.additionals && Array.isArray(item.additionals)) {
        const additionalsTotal = item.additionals.reduce(
          (addSum, additional) => addSum + (additional.unitPrice * additional.quantity),
          0
        );
        itemTotal += additionalsTotal;
      }
      
      return sum + itemTotal;
    }, 0);
  };

  const calculatePendingAmount = () => {
    if (!order) return 0;
    const totalPaid = order.payments
      ? order.payments.reduce((sum, item) => sum + item.amount, 0)
      : 0;
    return Math.max(0, order.totalAmount - totalPaid);
  };

  const handleAddPayment = async (
    payment: Omit<PaymentsOutput, "id" | "createdAt" | "updatedAt">
  ) => {
    if (!order) return;

    await paymentsService.create(payment);
    fetchOrderDetails();
  };

  const handleMarkAsPaid = async (
    method: PaymentDetailsMethod,
    remainingAmount: number
  ) => {
    if (!order) return;
    try {
      setIsLoading(true);
      const payment: Omit<PaymentsOutput, "id" | "createdAt" | "updatedAt"> = {
        orderId: order.id,
        method,
        amount: remainingAmount,
        paymentDate: new Date(),
      };
      await paymentsService.create(payment);
      fetchOrderDetails();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePayment = (paymentId: string) => {
    openDeleteDialog(paymentId); // Abre o dialog de confirmação
  };

  const handleConfirmDelete = async () => {
    if (!order || !itemToDelete) return;
    try {
      setIsLoading(true);
      await paymentsService.delete(itemToDelete);
      fetchOrderDetails();
    } finally {
      setIsLoading(false);
    }
    closeDeleteDialog(); // Fecha o dialog após a exclusão
  };

  // Função para formatar o número de telefone com WhatsApp
  const formatPhoneWithWhatsApp = (customer: CustomersOutput) => {
    if (!customer.phoneNumber) return null;

    const formattedPhone = formatDddPhone(
      customer.ddd,
      customer.phoneNumber,
      customer.internationalCode
    );
    const whatsappLink = formatWhatsappLink(
      customer.ddd,
      customer.phoneNumber,
      customer.internationalCode ?? "55"
    );

    return (
      <div className="flex items-center gap-2">
        <span>{formattedPhone}</span>
        {whatsappLink && (
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-green-500 hover:bg-green-600 text-white"
            onClick={(e) => e.stopPropagation()}
          >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
          </svg>
        </a>
        )}
      </div>
    );
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!order) {
    return <div>Pedido não encontrado</div>;
  }

  // Converter o pedido para os formatos de relatório
  const productionReport = orderToProductionReport(order);
  const receiptReport = orderToReceiptReport(order);

  const getStatusBadgeColor = (status: OrderStatus) => {
    switch (status) {
      case "PENDING":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "SCHEDULED":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "IN_PREPARATION":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "PREPARED":
        return "bg-teal-100 text-teal-800 border-teal-200";
      case "DONE":
        return "bg-green-100 text-green-800 border-green-200";
      case "CANCELED":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPaymentStatusBadgeColor = (status: string) => {
    switch (status) {
      case "PAID":
        return "bg-green-100 text-green-800 border-green-200";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "PARTIALLY_PAID":
        return "bg-orange-100 text-orange-800 border-orange-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const customer = order.customer as CustomersOutput;

  return (
    <>
      {/* Print Options Dialog */}
      <Dialog open={isPrintDialogOpen} onOpenChange={setIsPrintDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <PrintOptions
            productionReport={productionReport}
            receiptReport={receiptReport}
            onClose={() => setIsPrintDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <div className="space-y-6 max-w-6xl mx-auto px-4 py-6">
        {/* Print-only receipt view */}
        <div className="hidden print:block print:p-0 print:max-w-full print:m-0 print:bg-white print:text-black">
          <div className="text-center mb-6">
            <h1 className="text-xl font-bold">COMPROVANTE DE PEDIDO</h1>
            <p className="text-sm">Pedido #{order.id.substring(0, 8)}</p>
            <p className="text-sm">
              Data: {format(new Date(order.orderDate), "dd/MM/yyyy")}
            </p>
          </div>

          <div className="mb-4">
            <h2 className="font-bold border-b pb-1 mb-2">
              Informações do Cliente
            </h2>
            <p>
              <strong>Cliente:</strong>{" "}
              {customer ? customer.name : "Cliente não informado"}
            </p>
            {customer && customer.phoneNumber && (
              <p>
                <strong>Telefone:</strong>{" "}
                {formatDddPhone(
                  customer.ddd,
                  customer.phoneNumber,
                  customer.internationalCode
                )}
              </p>
            )}
          </div>

          <div className="mb-4">
            <h2 className="font-bold border-b pb-1 mb-2">
              Informações do Pedido
            </h2>
            <p>
              <strong>Status:</strong> {orderStatusTranslation[order.status]}
            </p>
            <p>
              <strong>Data de Preparação:</strong>{" "}
              {format(new Date(order.preparationDate), "dd/MM/yyyy")}
            </p>
            <p>
              <strong>Tipo de Entrega:</strong>{" "}
              {orderDeliveryTypeTransalation[order.delivery.deliveryType]}
            </p>
            {order.delivery.deliveryType === OrderDeliveryType.DELIVERY && (
              <>
                <p>
                  <strong>Data de Entrega:</strong>{" "}
                  {format(
                    new Date(order.delivery.deliveryAt!),
                    "dd/MM/yyyy - HH:mm"
                  )}
                </p>
                {order.delivery.address && (
                  <p>
                    <strong>Endereço:</strong>{" "}
                    {formatAddress(order.delivery.address)}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="mb-4">
            <h2 className="font-bold border-b pb-1 mb-2">Produtos</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-1">Produto</th>
                  <th className="text-center py-1">Qtd</th>
                  <th className="text-right py-1">Valor Unit.</th>
                  <th className="text-right py-1">Total</th>
                </tr>
              </thead>
              <tbody>
                {order.products.map((product) => (
                  <tr key={product.productId} className="border-b">
                    <td className="py-1">{product.name}</td>
                    <td className="text-center py-1">{product.quantity}</td>
                    <td className="text-right py-1">
                      {numberToMoney(product.unitPrice)}
                    </td>
                    <td className="text-right py-1">
                      {numberToMoney(product.unitPrice * product.quantity)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {order.description && (
              <div className="mt-3">
                <p>
                  <strong>Observações:</strong> {order.description}
                </p>
              </div>
            )}
          </div>

          <div className="mb-4">
            <h2 className="font-bold border-b pb-1 mb-2">Resumo Financeiro</h2>
            <div className="flex justify-between">
              <p>Subtotal:</p>
              <p>{numberToMoney(calculateTotalProductsValue())}</p>
            </div>
            {order.discount > 0 && (
              <div className="flex justify-between">
                <p>Desconto:</p>
                <p>-{numberToMoney(order.discount)}</p>
              </div>
            )}
            {order.additional > 0 && (
              <div className="flex justify-between">
                <p>Adicional:</p>
                <p>+{numberToMoney(order.additional)}</p>
              </div>
            )}
            {order.tax && order.tax > 0 && (
              <div className="flex justify-between">
                <p>Imposto:</p>
                <p>+{numberToMoney(order.tax)}</p>
              </div>
            )}
            {order.delivery.deliveryType === OrderDeliveryType.DELIVERY &&
              order.delivery.deliveryValue && (
                <div className="flex justify-between">
                  <p>Taxa de Entrega:</p>
                  <p>{numberToMoney(order.delivery.deliveryValue)}</p>
                </div>
              )}
            <div className="flex justify-between font-bold border-t pt-1 mt-1">
              <p>Total:</p>
              <p>{numberToMoney(order.totalAmount)}</p>
            </div>
            <div className="flex justify-between">
              <p>Status do Pagamento:</p>
              <p>{orderPaymentStatusTranslation[order.paymentStatus]}</p>
            </div>
          </div>

          <div className="text-center mt-8 text-sm">
            <p>Obrigado pela preferência!</p>
          </div>
        </div>

        {/* Regular view (hidden when printing) */}
        <div className="print:hidden">
          {/* Header with navigation and actions */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/pedidos")}
                className="h-8 w-8"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-2xl font-bold">Detalhes do Pedido</h1>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                onClick={() => setIsPrintDialogOpen(true)}
                variant="outline"
                size="sm"
                className="flex-1 sm:flex-none"
              >
                <Printer className="w-4 h-4 mr-2" />
                Imprimir
              </Button>
              <Button
                onClick={() => navigate(`/pedidos/${order.id}/editar`)}
                variant="default"
                size="sm"
                className="flex-1 sm:flex-none"
              >
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
            </div>
          </div>

          {/* Order summary card */}
          <Card
            className="overflow-hidden border-t-4"
            style={{
              borderTopColor:
                order.status === "DONE"
                  ? "#22c55e"
                  : order.status === "CANCELED"
                  ? "#ef4444"
                  : order.status === "PREPARED"
                  ? "#14b8a6"
                  : order.status === "IN_PREPARATION"
                  ? "#f97316"
                  : order.status === "SCHEDULED"
                  ? "#3b82f6"
                  : "#eab308",
            }}
          >
            <CardHeader className="pb-4 bg-muted/30">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <ClipboardListIcon className="w-5 h-5 text-primary" />
                    <CardTitle className="text-xl font-bold">
                      Pedido #{order.id.substring(0, 8)}
                    </CardTitle>
                  </div>
                  <p className="text-sm text-muted-foreground ml-7">
                    Criado em {format(new Date(order.orderDate), "dd/MM/yyyy")}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge
                          className={`${getStatusBadgeColor(
                            order.status
                          )} border px-3 py-1 flex items-center gap-1`}
                        >
                          <PackageCheck className="h-3 w-3 mr-1" />
                          <span>
                            Pedido: {orderStatusTranslation[order.status]}
                          </span>
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Status do pedido</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge
                          className={`${getPaymentStatusBadgeColor(
                            order.paymentStatus
                          )} border px-3 py-1 flex items-center gap-1`}
                        >
                          <CreditCard className="h-3 w-3 mr-1" />
                          <span>
                            Pagamento:{" "}
                            {orderPaymentStatusTranslation[order.paymentStatus]}
                          </span>
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Status do pagamento</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-0">
              <Tabs defaultValue="details" className="w-full">
                <TabsList className="w-full justify-start rounded-none border-b bg-transparent h-12 p-0">
                  <TabsTrigger
                    value="details"
                    className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none h-12"
                  >
                    Detalhes
                  </TabsTrigger>
                  <TabsTrigger
                    value="products"
                    className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none h-12"
                  >
                    Produtos
                  </TabsTrigger>
                  <TabsTrigger
                    value="payments"
                    className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none h-12"
                  >
                    Pagamentos
                  </TabsTrigger>
                </TabsList>

                {/* Details Tab */}
                <TabsContent value="details" className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Customer and Dates Section */}
                    <Card className="border shadow-sm">
                      <CardHeader className="pb-2 px-5 pt-5">
                        <CardTitle className="text-base flex items-center gap-2">
                          <UserIcon className="w-4 h-4 text-primary" />
                          Informações Gerais
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-2 px-5 pb-5 space-y-4">
                        <div className="space-y-3">
                          <InfoItem
                            icon={<UserIcon className="w-4 h-4 text-primary" />}
                            title="Cliente"
                            valueClassName={!customer ? "font-bold" : ""}
                            value={
                              customer ? customer.name : "Cliente não informado"
                            }
                          />

                          {customer && customer.phoneNumber && (
                            <InfoItem
                              icon={<Phone className="w-4 h-4 text-primary" />}
                              title="Telefone"
                              value={formatPhoneWithWhatsApp(customer)}
                            />
                          )}

                          <InfoItem
                            icon={
                              <CalendarIcon className="w-4 h-4 text-primary" />
                            }
                            title="Data de Preparação"
                            value={format(
                              new Date(order.preparationDate),
                              "dd/MM/yyyy"
                            )}
                          />

                          <InfoItem
                            icon={
                              <CalendarIcon className="w-4 h-4 text-primary" />
                            }
                            title="Data do pedido"
                            value={format(
                              new Date(order.orderDate),
                              "dd/MM/yyyy"
                            )}
                          />
                        </div>

                        {order.tags && order.tags.length > 0 && (
                          <div>
                            <div className="flex items-center space-x-2 mb-1">
                              <TagIcon className="w-4 h-4 text-primary" />
                              <h3 className="text-sm font-semibold">Tags</h3>
                            </div>
                            <div className="flex flex-wrap gap-1 ml-6 mt-1">
                              {(order.tags as TagsOutput[]).map((tag) => (
                                <Badge
                                  key={tag.id}
                                  style={{
                                    backgroundColor: tag.styling?.primaryColor,
                                    color: tag.styling?.fontColor,
                                    border: `1px solid ${tag.styling?.secondaryColor}`,
                                  }}
                                  className="text-xs px-2 py-0.5 rounded"
                                >
                                  {tag.name}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Delivery Section */}
                    <Card className="border shadow-sm">
                      <CardHeader className="pb-2 px-5 pt-5">
                        <CardTitle className="text-base flex items-center gap-2">
                          <TruckIcon className="w-4 h-4 text-primary" />
                          Informações de Entrega
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-2 px-5 pb-5 space-y-4">
                        <div className="space-y-3">
                          <InfoItem
                            icon={
                              <TruckIcon className="w-4 h-4 text-primary" />
                            }
                            title="Tipo de Entrega"
                            value={
                              <Badge variant="outline" className="font-normal">
                                {
                                  orderDeliveryTypeTransalation[
                                    order.delivery.deliveryType
                                  ]
                                }
                              </Badge>
                            }
                          />

                          {order.delivery.deliveryType ===
                            OrderDeliveryType.DELIVERY && (
                            <>
                              <InfoItem
                                icon={
                                  <CalendarIcon className="w-4 h-4 text-primary" />
                                }
                                title="Data/hora de entrega"
                                value={format(
                                  new Date(order.delivery.deliveryAt!),
                                  "dd/MM/yyyy - HH:mm"
                                )}
                              />

                              <InfoItem
                                icon={
                                  <DollarSignIcon className="w-4 h-4 text-primary" />
                                }
                                title="Valor da entrega"
                                value={numberToMoney(
                                  order.delivery?.deliveryValue || 0
                                )}
                                valueClassName="text-yellow-600"
                              />

                              {order.delivery.address && (
                                <InfoItem
                                  icon={
                                    <MapPinIcon className="w-4 h-4 text-primary" />
                                  }
                                  title="Endereço de Entrega"
                                  value={formatAddress(order.delivery.address)}
                                />
                              )}
                            </>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Status Update Section */}
                    <Card className="border shadow-sm md:col-span-2">
                      <CardHeader className="pb-2 px-5 pt-5">
                        <CardTitle className="text-base">
                          Atualizar Status
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-2 px-5 pb-5">
                        <OrderStatusUpdate
                          currentStatus={order.status}
                          onStatusUpdate={handleStatusUpdate}
                        />
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* Products Tab */}
                <TabsContent value="products" className="p-6">
                  <OrderProductList products={order.products} />

                  {/* Análise de Margem */}
                  <div className="mt-6">
                    <OrderMarginAnalysis
                      order={order}
                      additional={order.additional || 0}
                      discount={order.discount || 0}
                    />
                  </div>

                  {/* Observação na aba de produtos */}
                  {order.description && (
                    <Card className="border shadow-sm mt-6">
                      <CardHeader className="pb-2 px-5 pt-5">
                        <CardTitle className="text-base flex items-center gap-2">
                          <ClipboardIcon className="w-4 h-4 text-primary" />
                          Observações
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-2 px-5 pb-5">
                        <p className="text-sm text-muted-foreground">
                          {order.description}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* Payments Tab */}
                <TabsContent value="payments" className="p-6">
                  {/* Financial Summary */}
                  <Card className="border shadow-sm mb-6">
                    <CardHeader className="pb-2 px-5 pt-5">
                      <CardTitle className="text-base flex items-center gap-2">
                        <DollarSignIcon className="w-4 h-4 text-primary" />
                        Resumo Financeiro
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-2 px-5 pb-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        <InfoItem
                          icon={
                            <DollarSignIcon className="w-4 h-4 text-primary" />
                          }
                          title="Valor dos produtos"
                          value={numberToMoney(calculateTotalProductsValue())}
                          valueClassName="text-green-600"
                        />
                        <InfoItem
                          icon={
                            <DollarSignIcon className="w-4 h-4 text-red-600" />
                          }
                          title="Desconto"
                          value={`-${numberToMoney(order.discount)}`}
                          valueClassName="text-red-600"
                        />
                        <InfoItem
                          icon={
                            <DollarSignIcon className="w-4 h-4 text-blue-600" />
                          }
                          title="Valor Adicional"
                          value={`+${numberToMoney(order.additional)}`}
                          valueClassName="text-blue-600"
                        />

                        {order.tax && (
                          <InfoItem
                            icon={
                              <DollarSignIcon className="w-4 h-4 text-blue-600" />
                            }
                            title="Imposto"
                            value={`+${numberToMoney(order.tax)}`}
                            valueClassName="text-blue-600"
                          />
                        )}

                        <InfoItem
                          icon={
                            <DollarSignIcon className="w-4 h-4 text-yellow-600" />
                          }
                          title="Valor da entrega"
                          value={numberToMoney(
                            order.delivery.deliveryValue || 0
                          )}
                          valueClassName="text-yellow-600"
                        />

                        <InfoItem
                          icon={
                            <DollarSignIcon className="w-4 h-4 text-primary" />
                          }
                          title="Valor Total"
                          value={numberToMoney(order.totalAmount)}
                          valueClassName="text-lg font-bold text-green-800"
                        />
                        <InfoItem
                          icon={
                            <DollarSignIcon className="w-4 h-4 text-primary" />
                          }
                          title="Pagamento pendente"
                          value={numberToMoney(calculatePendingAmount())}
                          valueClassName={
                            calculatePendingAmount() > 0
                              ? "text-orange-500 font-semibold"
                              : "text-green-600 font-semibold"
                          }
                        />
                      </div>
                    </CardContent>
                  </Card>

                  <OrderPayments
                    orderId={order.id}
                    totalAmount={order.totalAmount}
                    payments={order.payments || []}
                    paymentStatus={order.paymentStatus}
                    onAddPayment={handleAddPayment}
                    onMarkAsPaid={handleMarkAsPaid}
                    onDeletePayment={handleDeletePayment}
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Componente de confirmação de exclusão */}
        <DeleteConfirmationAlert
          isOpen={isOpen}
          onClose={closeDeleteDialog}
          onConfirm={handleConfirmDelete}
          title="Confirmar Exclusão de Pagamento"
          message="Tem certeza que deseja excluir este pagamento? Esta ação não pode ser desfeita."
        />
      </div>
    </>
  );
}

function InfoItem({ icon, title, value, valueClassName = "" }: InfoItemProps) {
  return (
    <div>
      <div className="flex items-center space-x-1 mb-1">
        {icon}
        <h3 className="text-xs font-semibold">{title}</h3>
      </div>
      <div className={`text-sm ml-5 ${valueClassName}`}>{value}</div>
    </div>
  );
}
